// The review page's rules, apart from React: how plan text becomes chips, what needs confirming, what blocks approval,
// and the plan that gets saved (the reviewer's removals, medication statuses and dose choices applied).

export const STATUS = { working: "Working", ruled_out: "Ruled out", possible: "Possible", likely: "Likely", contributing: "Contributing",
  not_present_now: "Not present now", part_of_picture: "Part of the picture" };
export const OUTCOME = { supports: "Supports", contradicts: "Goes against", no_difference: "No difference", too_few_data: "Too few data" };
export const OWNER = { member: "Member", care_team: "Care team", doctor: "Doctor" };
export const MED_STATUS = { current: "Current", past: "Past", planned: "Planned", unclear: "Unclear" };

// Ids the analyst cites: metric paths, comparisons, facts (F1), quotes (Q1), notes (N1), journal (J1), etc.
const ID = String.raw`\b(cmp\.\d+|(?:glucose|food|steps|sleep|meds|weights|app_daily_scores|wish_journey)\.[a-z0-9_]+(?:\.[a-z0-9_]+)*|meal_responses|weights|app_daily_scores|[FQNJEPA]\d+)\b`;

// "text (cmp.1, F2)" -> text and chips; {{m:id}}-style tokens and bare ids anywhere become chips too.
// Returns segments: {text} | {chip: id} | {tag: label}. A parenthesized list becomes chips only when every id resolves.
export function parseRich(text, lookup) {
  const rx = new RegExp(String.raw`\{\{([mfqp]):([^}]+)\}\}|\s?\(([A-Za-z_.0-9]+(?:,\s*[A-Za-z_.0-9]+)*)\)|` + ID, "g");
  const src = String(text ?? "");
  const out = [];
  let last = 0, m;
  const push = (seg) => out.push(seg);
  while ((m = rx.exec(src))) {
    let rep = null;
    if (m[1]) rep = m[1] === "p" ? [{ tag: m[2] }] : [{ chip: m[2] }];
    else if (m[3]) {
      const ids = m[3].split(/,\s*/);
      if (ids.every((i) => lookup[i])) rep = [{ text: " " }, ...ids.flatMap((i, k) => (k ? [{ text: " " }, { chip: i }] : [{ chip: i }]))];
    } else if (lookup[m[0]]) rep = [{ chip: m[0] }];
    if (rep === null) continue;
    if (m.index > last) push({ text: src.slice(last, m.index) });
    rep.forEach(push);
    last = m.index + m[0].length;
  }
  if (last < src.length) push({ text: src.slice(last) });
  return out;
}

// Confirmation keys, by plan section, so a revision can clear the ones it touched.
// Plan-ahead items can be removed, which shifts the positions of the rest once saved, so their ticks are kept by text.
export const SECTION_KEYS = { headline: ["headline"], lever: ["lever"], check: ["check"], doctor_ask: ["doctor_ask"],
  safety: ["safety."], longer_game: ["lg:"], people_mentioned: ["person."] };
export const lgKey = (it) => `lg:${it.item}`;
const allRemoved = (items, removed, pre) => items.length > 0 && items.every((_, i) => removed[pre + i]);

export function requiredKeys(plan, meds, removed) {
  const k = ["headline", "lever", "check"];
  if (plan.doctor_ask.present && !allRemoved(plan.doctor_ask.items, removed, "da.")) k.push("doctor_ask");
  if (Object.keys(meds || {}).length) k.push("meds");
  plan.safety.forEach((_, i) => k.push(`safety.${i}`));
  plan.longer_game.forEach((it, i) => { if (it.owner === "member" && !removed[`lg.${i}`]) k.push(lgKey(it)); });
  plan.people_mentioned.forEach((_, i) => k.push(`person.${i}`));
  return k;
}

export function chosenDose(conflict, choice) {
  if (!conflict || !choice) return null;
  return choice.pick === "app" ? conflict.app : choice.pick === "notes" ? conflict.notes : (choice.other || "");
}

// A dose already confirmed on the server (after a reload) shows as the reviewer's pick.
export function initialDoseChoices(conflicts) {
  const out = {};
  (conflicts || []).forEach((c) => {
    if (!c.confirmed) return;
    out[c.id] = c.confirmed === c.app ? { pick: "app" } : c.confirmed === c.notes ? { pick: "notes" } : { pick: "other", other: c.confirmed };
  });
  return out;
}

export function blockers(plan, ui, review) {
  const req = requiredKeys(plan, review.meds, ui.removed);
  const done = req.filter((k) => ui.confirmed[k]).length;
  const b = [];
  if (done < req.length) b.push(`${req.length - done} required item${req.length - done > 1 ? "s" : ""} to confirm`);
  plan.placeholders.forEach((p) => { if (p.required && !(p.value || "").trim()) b.push(`fill "${p.name}"`); });
  (review.dose_conflicts || []).forEach((c) => { if (!(chosenDose(c, ui.doseChoice[c.id]) || "").trim()) b.push(`choose ${c.name}'s dose`); });
  if (!ui.name.trim()) b.push("your name");
  if (!ui.purpose) b.push("who it's for");
  return { req, done, b };
}

// The plan as the reviewer left it: what's saved, previewed and approved.
export function finalPlan(plan, ui, review) {
  const p = JSON.parse(JSON.stringify(plan));
  p.medications = p.medications || [];
  (review.dose_conflicts || []).forEach((c) => {
    const g = p.medications.find((m) => m.id === c.id), d = chosenDose(c, ui.doseChoice[c.id]);
    if (g) g.confirmed_dose = d && d.trim() ? d.trim() : null;
  });
  Object.entries(ui.medStatus).forEach(([id, v]) => {
    const g = p.medications.find((m) => m.id === id);
    if (g) Object.assign(g, { status: v, source_id: "reviewer", excerpt: "" });
    else p.medications.push({ id, status: v, source_id: "reviewer", excerpt: "" });
  });
  const keep = (arr, pre) => arr.filter((_, i) => !ui.removed[pre + i]);
  p.differential = keep(p.differential, "dx.");
  p.longer_game = keep(p.longer_game, "lg.");
  p.doctor_ask.items = keep(p.doctor_ask.items, "da.");
  if (p.doctor_ask.present && !p.doctor_ask.items.length)  // every item removed: no doctor section (the schema needs one or the other)
    Object.assign(p.doctor_ask, { present: false, urgency: "none", no_ask_because: "The reviewer removed every doctor item." });
  return p;
}

// Candidates whose status the reviewer changed since the plan was loaded: {name: status}.
export function statusChanges(plan, original) {
  const out = {};
  plan.differential.forEach((c, i) => {
    const was = original[i];
    if (was && was.name === c.name && was.status !== c.status) out[c.name] = c.status;
  });
  return out;
}

export function clearConfirmations(confirmed, sections) {
  const prefixes = sections.flatMap((s) => SECTION_KEYS[s] || []);
  return Object.fromEntries(Object.entries(confirmed).filter(([k]) => !prefixes.some((p) => (/[.:]$/.test(p) ? k.startsWith(p) : k === p))));
}

export function getPath(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? o : o[Number.isNaN(+k) ? k : +k]), obj);
}

export function setPath(obj, path, value) {
  const out = JSON.parse(JSON.stringify(obj));
  const ks = path.split(".");
  const last = ks.pop();
  const parent = ks.reduce((o, k) => o[Number.isNaN(+k) ? k : +k], out);
  parent[Number.isNaN(+last) ? last : +last] = value;
  return out;
}

export const fmtDate = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

export function elapsed(fromIso, now = Date.now()) {
  const s = Math.max(0, Math.round((now - new Date(fromIso).getTime()) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}
